import { ageCommonEvent, BusEvents, EventSource } from "@/common/buses";
import { SearchInitiator, useAnalyticsStore } from "@/common/store/analyticsStore";
import { useAppStore } from "@/common/store/appStore";
import { FirehoseProperties } from "./eventTracker";

type FirehoseProjection<K extends keyof BusEvents> =
  (payload: BusEvents[K]) => FirehoseProperties | null;

interface FirehoseEventBase {
  name: string;
  allowInEmbed?: boolean;
}

type FirehoseEvent<K extends keyof BusEvents> = [BusEvents[K]] extends [undefined]
  ? FirehoseEventBase & { properties?: FirehoseProjection<K> }
  : FirehoseEventBase & { properties: FirehoseProjection<K> };

type FirehoseCatalog = {
  [K in keyof BusEvents]: FirehoseEvent<K> | null;
};

const NOT_SENT = null;

const isFullscreen = () => useAppStore.getState().isFullScreen;

const searchTag = () => useAnalyticsStore.getState().searchTag ?? null;

const eventSource = () => useAnalyticsStore.getState().eventSource?.name ?? EventSource.Other;

const isWatchPage = () => window.location.pathname.startsWith("/watch/");

const ageVerification = ({ country, state, source }: ageCommonEvent) => ({ country, state, source });

export const FIREHOSE_EVENTS: FirehoseCatalog = {
  gifView: NOT_SENT,
  meaningfulGifView: NOT_SENT,
  gifReplay: NOT_SENT,

  forceFullscreenEntered: { name: "force_fullscreen_entered" },
  forceFullscreenExited: { name: "force_fullscreen_exited" },

  dailyVisit: { name: "feed_daily_visit", allowInEmbed: true, properties: () => ({}) },

  gifClicked: { name: "gif_clicked", properties: ({ source, position }) => ({ source, position }) },
  gifLiked: {
    name: "gif_liked",
    properties: ({ gifId, source, sourceId, boosted, position, playCount, isMainGIF }) => ({
      gif_id: gifId,
      source,
      source_id: sourceId,
      boosted,
      position,
      play_count: playCount,
      is_main_gif: isWatchPage() ? isMainGIF : null,
      fullscreen: isFullscreen(),
      search_tag: searchTag()
    })
  },
  gifUnliked: {
    name: "gif_unliked",
    properties: ({ gifId, isMainGIF }) => ({
      gif_id: gifId,
      is_main_gif: isWatchPage() ? isMainGIF : null,
      source: eventSource(),
      fullscreen: isFullscreen(),
      search_tag: searchTag()
    })
  },
  addedToCollection: {
    name: "gif_added_to_collection",
    properties: ({ gifId, source, sourceId, boosted, position, playCount }) => ({
      gif_id: gifId,
      source,
      source_id: sourceId,
      boosted,
      position,
      play_count: playCount,
      fullscreen: isFullscreen(),
      search_tag: searchTag()
    })
  },

  adLoaded: { name: "ad_loaded", properties: ({ isLoaded }) => ({ is_loaded: isLoaded }) },

  infoBarClicked: { name: "info_bar_clicked", properties: ({ announcement }) => ({ announcement }) },
  infoBarClosed: { name: "info_bar_closed", properties: ({ announcement }) => ({ announcement }) },

  sharedFixedEmbed: { name: "gif_fixed_embed_clicked" },
  sharedResponsiveEmbed: { name: "gif_responsive_embed_clicked" },
  shareClick: { name: "gif_share_menu_opened" },
  shareCopy: { name: "gif_link_copied", properties: ({ gif }) => ({ gif_id: gif }) },
  shareDiscord: { name: "gif_link_copied_for_discord", properties: ({ gif }) => ({ gif_id: gif }) },
  shareEmbed: { name: "gif_embed_dialog_opened" },
  shareReddit: { name: "gif_shared_to_reddit", properties: ({ gif }) => ({ gif_id: gif }) },
  shareTwitter: { name: "gif_shared_to_twitter", properties: ({ gif }) => ({ gif_id: gif }) },
  pinnedContentClicked: { name: "own_pinned_gif_clicked" },
  menuButtonClicked: { name: "menu_button_clicked", properties: ({ button, device, source_page }) => ({ button, device, source_page }) },
  gridNewTabOpened: {
    name: "grid_new_tab_opened",
    properties: ({ target, gif, position, method, device, source }) => ({
      target,
      gif: gif ?? null,
      position,
      method,
      device,
      source
    })
  },
  pageNewTabOpened: { name: "page_new_tab_opened", properties: ({ device }) => ({ device }) },
  followUserClicked: {
    name: "user_followed",
    properties: ({ userName, source, sourceId, gifId, position, playCount }) => ({
      creator: userName,
      source,
      source_id: sourceId,
      gif_id: gifId,
      position,
      play_count: playCount,
      search_tag: searchTag()
    })
  },
  followUserClickedAnonymously: {
    name: "user_follow_attempted_anonymously",
    properties: ({ userName, source, sourceId }) => ({ creator: userName, source, source_id: sourceId, search_tag: searchTag() })
  },
  unfollowUserClicked: {
    name: "user_unfollowed",
    properties: ({ userName, source, sourceId }) => ({ creator: userName, source, source_id: sourceId, search_tag: searchTag() })
  },
  socialProfileClicked: {
    name: "social_profile_clicked",
    properties: ({ userName, platform, originGif }) => ({
      creator: userName,
      platform,
      origin_gif_id: originGif?.id ?? null
    })
  },
  profileClicked: {
    name: "profile_clicked",
    properties: ({ gifId, creator, source, sourceId, position, playCount, promoted }) => ({
      gif_id: gifId,
      creator,
      source,
      source_id: sourceId,
      position,
      play_count: playCount,
      promoted,
      search_tag: searchTag()
    })
  },
  tagClicked: { name: "tag_clicked", properties: ({ tag, section }) => ({ tag, section, source: eventSource() }) },

  signUpSuccess: { name: "registration_completed", properties: ({ source }) => ({ source }) },
  loginCompleted: { name: "login_completed" },

  searchBarOpened: { name: "search_bar_opened" },
  searched: { name: "search_suggestions_returned", properties: ({ totalCount }) => ({ total_count: totalCount }) },
  searchResultClicked: {
    name: "search_result_clicked",
    properties: ({ section, title, position, initiator }) => ({
      section,
      title,
      position,
      initiator: initiator ?? SearchInitiator.Other
    })
  },
  searchQuerySubmitted: {
    name: "search_query_submitted",
    properties: ({ query, queryType, queryCount, itemId }) => ({
      query,
      query_type: queryType,
      term_count: queryCount,
      item_id: itemId
    })
  },
  seeAllButtonClicked: {
    name: "see_all_button_clicked",
    properties: ({ section, initiator }) => section
      ? { section, initiator, search_tag: searchTag() }
      : null
  },

  tagFeedbackEnabledOptions: { name: "tag_feedback_options_opened" },

  uploadTagSelectionStarted: { name: "upload_tag_selection_started", properties: ({ verified }) => ({ verified }) },
  uploadTagSelectionEnded: { name: "upload_tag_selection_ended", properties: ({ count, verified }) => ({ count, verified }) },
  uploadVideoTrimmed: { name: "upload_video_trimmed", properties: ({ verified }) => ({ verified }) },
  uploadSourceConfirmed: { name: "upload_source_confirmed", properties: ({ type, verified }) => ({ type, verified }) },
  uploadSourceDropped: { name: "upload_source_dropped", properties: ({ type, verified }) => ({ type, verified }) },

  gifWatched120s: { name: "watch_time_120s_reached" },
  profileViewed: { name: "profile_viewed", properties: ({ userName }) => ({ creator: userName }) },

  PWASessionStarted: { name: "pwa_session_started" },
  PWAPageVisited: { name: "pwa_page_visited" },

  watchPageNavigation: {
    name: "player_link_clicked",
    properties: ({ elementType, targetURL, source }) => ({ element_type: elementType.toLowerCase(), target_url: targetURL, source })
  },

  verificationSubmit: { name: "creator_verification_submitted" },

  pageClosed: {
    name: "page_closed",
    properties: ({ page, type, gifsViewed, gifsViewedMeaningful, timeSpent, contentLength, searchTag }) => ({
      page,
      type,
      gifs_viewed: gifsViewed,
      meaningful_gifs_viewed: gifsViewedMeaningful,
      time_spent: timeSpent,
      content_length: contentLength,
      search_tag: searchTag ?? null
    })
  },

  nicheGifVoteUpClicked: { name: "niche_gif_vote_up_clicked", properties: ({ gif, niche }) => ({ gif_id: gif, niche_id: niche, search_tag: searchTag() }) },
  nicheGifVoteDownClicked: { name: "niche_gif_vote_down_clicked", properties: ({ gif, niche }) => ({ gif_id: gif, niche_id: niche, search_tag: searchTag() }) },
  nicheGifsOrderChanged: { name: "niche_gifs_order_changed", properties: ({ nicheId, order }) => ({ niche_id: nicheId, order }) },
  joinNicheClickedAnonymously: {
    name: "niche_subscription_attempted_anonymously",
    properties: ({ nicheId }) => ({ niche_id: nicheId, source: eventSource(), search_tag: searchTag() })
  },
  nicheUnsubscribed: {
    name: "niche_unsubscribed",
    properties: ({ nicheId, origin }) => ({ niche_id: nicheId, origin, search_tag: searchTag() })
  },
  nicheSubscriptionModuleShown: { name: "niche_subscription_module_shown", properties: ({ nicheId }) => ({ niche_id: nicheId }) },
  nichePreviewClicked: { name: "niche_preview_clicked", properties: ({ nicheId, source, initiator }) => ({ niche_id: nicheId, source, initiator }) },
  allNichesPageViewed: { name: "all_niches_page_viewed" },
  nicheOpened: { name: "niche_opened", properties: ({ nicheId, source }) => ({ niche_id: nicheId, source }) },
  nicheSubscribed: {
    name: "niche_subscribed",
    properties: ({ nicheId, origin, source }) => ({ niche_id: nicheId, origin, source, search_tag: searchTag() })
  },

  nicheCategoryClicked: { name: "niche_category_clicked", properties: ({ category, position }) => ({ category, position }) },

  ofModuleShown: { name: "of_module_shown", properties: ({ placement, device, source }) => ({ placement, device, source }) },

  ageVerificationStarted: { name: "age_verification_started", properties: ageVerification },
  ageVerificationCompleted: { name: "age_verification_completed", properties: ageVerification },
  ageVerificationAbandoned: { name: "age_verification_abandoned", properties: ageVerification },
  ageGateRedirected: { name: "age_gate_redirected", properties: ({ country, state }) => ({ country, state }) },
  ageVerificationPromptShown: { name: "age_verification_prompt_shown", properties: ageVerification },
  ageVerificationFailed: { name: "age_verification_failed", properties: ageVerification },
  ageVerificationLiteStayed: { name: "age_verification_lite_stayed", properties: ageVerification },
  enterRedgifsClicked: { name: "enter_redgifs_clicked", properties: ageVerification },

  csatSurveyShown: { name: "csat_survey_shown", properties: ({ version }) => ({ version }) },
  csatSurveySkipped: { name: "csat_survey_skipped", properties: ({ version }) => ({ version }) },
  csatSurveySubmitted: { name: "csat_survey_submitted", properties: ({ version }) => ({ version }) },

  registrationBannerImpression: { name: "registration_banner_shown" },
  registrationBannerClick: { name: "registration_banner_clicked" },
  registrationBannerClosed: { name: "registration_banner_closed" },
  registrationStarted: { name: "registration_started", properties: ({ source }) => ({ source }) },
  registrationSubmitted: { name: "registration_submitted", properties: ({ source }) => ({ source }) },
  registrationVerificationCodeSent: { name: "registration_verification_code_sent", properties: ({ source }) => ({ source }) },

  feedSwitched: { name: "feed_navigation", properties: ({ src, dest }) => ({ src, dest }) },

  profileGoToTopShown: { name: "profile_go_to_top_shown", properties: ({ source, creator }) => ({ source, creator }) },
  profileGoToTopClicked: { name: "profile_go_to_top_clicked", properties: ({ source, creator }) => ({ source, creator }) },

  playerSocialLinkClicked: { name: "player_social_link_clicked", properties: ({ creator, platform, gif }) => ({ creator, platform, gif }) },

  redgifsAiClicked: { name: "redgifs_ai_clicked", properties: ({ spot }) => ({ spot }) },

  gifPlayback: NOT_SENT,
  gifVideoLoad: NOT_SENT,
  fullscreenEntered: NOT_SENT,
  fullscreenExited: NOT_SENT,
  watchPageVisited: NOT_SENT,
  firstVisit: NOT_SENT,
  homePageVisited: NOT_SENT,
  managePageVisited: NOT_SENT,
  gifShared: NOT_SENT,
  optionClicked: NOT_SENT,
  boostProfileClicked: NOT_SENT,
  loggedOut: NOT_SENT,
  uploadSelected: NOT_SENT,
  uploadStarted: NOT_SENT,
  uploadEnded: NOT_SENT,
  uploadAudienceDefined: NOT_SENT,
  uploadFinished: NOT_SENT,
  voteUpAddClicked: NOT_SENT,
  voteUpRemoveClicked: NOT_SENT,
  voteDownAddClicked: NOT_SENT,
  voteDownRemoveClicked: NOT_SENT,
  privateSessionStarted: NOT_SENT,
  userFetched: NOT_SENT,
  deviceTracked: NOT_SENT,
  experimentVariantExposure: {
    name: "experiment_variant_exposure",
    properties: ({ experimentKey, experimentVariant }) => ({
      experiment_key: experimentKey,
      experiment_variant: String(experimentVariant)
    })
  },
  playerNicheClicked: NOT_SENT,
  playerTagClicked: NOT_SENT,
  orientationChanged: NOT_SENT,
  joinNicheClicked: NOT_SENT,
  leaveNicheClicked: NOT_SENT,
  manageItemInteracted: NOT_SENT,
  managePageInteracted: NOT_SENT,
  liveCircleClicked: NOT_SENT,
  liveCamClicked: NOT_SENT,
  liveCamView: NOT_SENT,
  paidLinksClicked: NOT_SENT,
  paidLinksViewed: NOT_SENT,
  embedRevenueLinkClicked: NOT_SENT,
};
